import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";

function formatDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("es", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    }).format(date);
}

export default function NotificationBell({ dark = false, responsive = false }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications", { params: { limit: 40 } });
            setItems(data.items || []);
            setUnread(data.unread_count || 0);
        } catch {
            // El resto del CRM no debe bloquearse si la bandeja no está disponible.
        }
    }, []);

    useEffect(() => {
        load();
        const timer = window.setInterval(load, 30000);
        return () => window.clearInterval(timer);
    }, [load]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const openNotification = async (notification) => {
        if (!notification.read_at) {
            try { await api.patch(`/notifications/${notification.notification_id}/read`); } catch { /* noop */ }
            setItems((prev) => prev.map((item) => item.notification_id === notification.notification_id ? { ...item, read_at: new Date().toISOString() } : item));
            setUnread((count) => Math.max(0, count - 1));
        }
        setOpen(false);
        if (notification.link) navigate(notification.link);
    };

    const readAll = async () => {
        try {
            await api.post("/notifications/read-all");
            const now = new Date().toISOString();
            setItems((prev) => prev.map((item) => ({ ...item, read_at: item.read_at || now })));
            setUnread(0);
        } catch { /* noop */ }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={unread ? `${unread} notificaciones sin leer` : "Notificaciones"}
                    className={`relative p-2 rounded-md transition-colors ${
                        responsive
                            ? "text-slate-200 hover:bg-slate-800 md:text-slate-600 md:hover:bg-white md:hover:text-slate-900"
                            : dark ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-white hover:text-slate-900"
                    }`}
                    data-testid="notifications-button"
                >
                    <Bell className="w-5 h-5" />
                    {unread > 0 && (
                        <span className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ${responsive ? "ring-slate-900 md:ring-white" : dark ? "ring-slate-900" : "ring-white"}`} data-testid="notifications-unread-dot" />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(92vw,390px)] p-0" data-testid="notifications-popover">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div>
                        <h3 className="font-semibold text-slate-900">Notificaciones</h3>
                        <p className="text-xs text-slate-500">{unread ? `${unread} sin revisar` : "Todo revisado"}</p>
                    </div>
                    {unread > 0 && (
                        <Button variant="ghost" size="sm" onClick={readAll} className="text-xs">
                            <CheckCheck className="w-4 h-4 mr-1" /> Marcar todas
                        </Button>
                    )}
                </div>
                <ScrollArea className="h-[420px]">
                    {items.length === 0 ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Inbox className="w-9 h-9 mb-2" />
                            <p className="text-sm">No tienes notificaciones</p>
                        </div>
                    ) : items.map((notification) => (
                        <button
                            type="button"
                            key={notification.notification_id}
                            onClick={() => openNotification(notification)}
                            className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors hover:bg-slate-50 ${notification.read_at ? "bg-white" : "bg-orange-50/60"}`}
                        >
                            <div className="flex gap-3">
                                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${notification.read_at ? "bg-slate-300" : "bg-red-500"}`} />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-900">{notification.title}</p>
                                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{notification.message}</p>
                                    <p className="text-[11px] text-slate-400 mt-1.5">{formatDate(notification.created_at)}</p>
                                </div>
                            </div>
                        </button>
                    ))}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}
